import styles from './index.module.css'

import React from 'react'

import { FilterComponentProps } from 'types/filter-page'

import { Flex, Text } from 'components/ds'

import { useKeyPress } from 'hooks/use-key-press'

import { FilterErrorText } from '../components'
import { NumericInput } from '../components/numeric-input'
import { useNumericValidation } from '../number/use-numeric-validation'

const DurationForm = ({
  value,
  onChange,
  operator = 'between',
  onPress,
  onValidityChange,
}: FilterComponentProps) => {
  const { handleKeyDown } = useKeyPress('Enter', onPress)

  // Validation always runs in 'between' mode: both bounds are always shown, and
  // the committed operator flips as bounds fill (see commit), which must not
  // reset the hook's per-slot state mid-edit.
  const { letterErrors, hasActiveLetterError, handleDigitChange, rawText } =
    useNumericValidation({ operator: 'between', value, onValidityChange })

  // A single filled bound collapses to a plain 'gte'/'lte' value (badge reads
  // "is at least/at most X") instead of a 'between' range with a missing side —
  // e.g. "under 60s" or "at least 90s" shouldn't require both bounds.
  const committedStart: string =
    operator === 'gte'
      ? value || ''
      : operator === 'between'
      ? value?.[0] || ''
      : ''
  const committedEnd: string =
    operator === 'lte'
      ? value || ''
      : operator === 'between'
      ? value?.[1] || ''
      : ''

  const start = rawText.start ?? committedStart
  const end = rawText.end ?? committedEnd

  const commit = (newStart: string, newEnd: string) => {
    if (newStart && newEnd) {
      onChange([newStart, newEnd], 'between')
    } else if (newStart) {
      onChange(newStart, 'gte')
    } else if (newEnd) {
      onChange(newEnd, 'lte')
    } else {
      onChange(undefined, 'between')
    }
  }

  return (
    <Flex direction="column" gap="1">
      <Flex gap="2" align="center" className={styles.container}>
        <NumericInput
          value={start}
          onValueChange={raw =>
            handleDigitChange('start', raw, digits =>
              commit(digits, committedEnd),
            )
          }
          onKeyDown={handleKeyDown}
          placeholder="Min seconds"
          autoFocus
          hasError={letterErrors.start}
          width="fluid"
        />
        <Text size="2">-</Text>
        <NumericInput
          value={end}
          onValueChange={raw =>
            handleDigitChange('end', raw, digits =>
              commit(committedStart, digits),
            )
          }
          onKeyDown={handleKeyDown}
          placeholder="Max seconds"
          hasError={letterErrors.end}
          width="fluid"
        />
      </Flex>
      {hasActiveLetterError && (
        <FilterErrorText>Only numbers allowed</FilterErrorText>
      )}
    </Flex>
  )
}

export default DurationForm
